import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { toast } from 'sonner';

import {
  useLazyGetReceiveReleaseContextQuery,
  useReleaseReceivedGoodsMutation,
  type ReceiveReleaseContextDto,
  type ReceiveReleaseResultDto
} from '@/entities/receive';
import { useConfirmArrivalAttachments } from '@/features/receive-confirm-arrival';
import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';

import { buildReleaseBody, buildReleaseDefaults, clampToReleasable } from '../lib/build-payload';
import { getReleaseGoodsSchema } from './validation';
import type { ReleaseGoodsFormValues } from './types';

interface UseReleaseGoodsFormArgs {
  /** The prefill confirm-arrival answered with; re-read from the backend after a refused release. */
  context: ReceiveReleaseContextDto;
  /** Fired after a successful release — the dashboard uses it to drop stale branches. */
  onSuccess?: (result: ReceiveReleaseResultDto) => void;
}

export const useReleaseGoodsForm = ({ context: initialContext, onSuccess }: UseReleaseGoodsFormArgs) => {
  const { t, i18n } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const [context, setContext] = useState(initialContext);
  const [result, setResult] = useState<ReceiveReleaseResultDto | null>(null);

  // Today on the backend's clock when it says so; the browser's otherwise.
  const maxReleaseDate = useMemo(() => context.maxReleaseDate ?? format(new Date(), 'yyyy-MM-dd'), [context]);

  const schema = useMemo(() => getReleaseGoodsSchema(t, context, maxReleaseDate), [t, context, maxReleaseDate]);

  const form = useForm<ReleaseGoodsFormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildReleaseDefaults(initialContext, maxReleaseDate)
  });

  const attachments = useConfirmArrivalAttachments();
  const [releaseGoods, { isLoading: isSubmitting }] = useReleaseReceivedGoodsMutation();
  const [fetchContext] = useLazyGetReceiveReleaseContextQuery();

  const { setValue, getValues } = form;

  /** Clears every quantity — the legacy "reset quantities" link. */
  const handleResetQuantities = useCallback(() => {
    setValue(
      'lines',
      getValues('lines').map((line) => ({ ...line, goodQty: 0, damagedQty: 0 })),
      { shouldDirty: true, shouldValidate: true }
    );
  }, [getValues, setValue]);

  /**
   * There is no idempotency key, and a refusal usually means the stock moved
   * (`STOCK_NOT_AVAILABLE`, or a quantity above what is now releasable). The
   * ceilings are re-read so a retry is checked against the stock as it is.
   */
  const refreshContext = useCallback(async () => {
    if (!projectId || !siteId) return;
    try {
      const fresh = await fetchContext({
        receiveShipmentIds: context.receiveShipmentIds,
        projectId,
        siteId,
        lang: i18n.language
      }).unwrap();
      setContext(fresh);
      setValue('lines', clampToReleasable(getValues('lines'), fresh), { shouldValidate: true });
    } catch {
      // The refusal is already on screen; a stale form is still better than none.
    }
  }, [projectId, siteId, fetchContext, context.receiveShipmentIds, i18n.language, setValue, getValues]);

  const submit = useCallback(
    async (values: ReleaseGoodsFormValues) => {
      if (!projectId || !siteId) return;
      const { pending, comment } = attachments;

      try {
        const released = await releaseGoods({
          projectId,
          siteId,
          lang: i18n.language,
          body: buildReleaseBody(values, context),
          files: pending.map((item) => item.file),
          attachmentTypes: pending.map((item) => item.attachmentType),
          attachmentComment: comment.trim() || undefined
        }).unwrap();

        setResult(released);
        attachments.reset();
        onSuccess?.(released);
        toast.success(t('receive.release.success', 'Goods released'));
      } catch (error) {
        // The receipt stays booked whatever happens here, so the form stays up
        // for a correction and a retry.
        toast.error(
          getApiErrorMessage(
            error,
            t('receive.release.error.submitFailed', 'Failed to release the goods. Please try again.')
          )
        );
        void refreshContext();
      }
    },
    [projectId, siteId, attachments, releaseGoods, i18n.language, context, onSuccess, t, refreshContext]
  );

  return {
    form,
    context,
    attachments,
    result,
    maxReleaseDate,
    isSubmitting,
    handleResetQuantities,
    handleSubmit: form.handleSubmit(submit)
  };
};
